// Pure helpers behind Predict's watch list (STRY0038309). The page reads
// GET /predict/watchlist, /predict/bug and /predict/counts; each list bug is
// {vendor, bugId, title, url, filed, score, promoted, reason, cve,
// weaknessType, scorerVersion, source, cwe, target, targetName,
// inventoryVendorId, inventoryProducts, inventoryVendorIds}, and the list's
// `inventory` says, per archive vendor id, how each Prevent vendor's
// inventory puts that project in scope.

// The source labels live in predictSources.js, shared with STRY0039090's
// dashboard and bug page; the watch list and its filter read them from here.
import { sourceFromUrl, sourceLabel } from './predictSources';

export { sourceFromUrl, sourceLabel };

// The score at which Predict promotes a bug. It is the same for every customer:
// a customer's threshold only decides which bugs reach their list.
export const PROMOTION_SCORE = 6.0;

export const DEFAULT_SORT = { key: 'score', dir: 'desc' };

// The customer's threshold (STRY0038971). GET /predict/threshold always sends
// its own range; this is what the page assumes when it hasn't answered.
export const THRESHOLD_RANGE = { default: 6.0, min: 5.0, max: 9.0, step: 0.5 };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The wire's dates are ISO 8601 in UTC (YYYY-MM-DDTHH:MM:SS.ffffffZ). They are
// read as text, not through Date, so the day shown is the UTC day wherever the
// browser is.
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

export const isScore = score => typeof score === 'number' && Number.isFinite(score);

export const formatScore = score => (isScore(score) ? score.toFixed(1) : '—');

const numberOr = (value, fallback) => (isScore(value) ? value : fallback);

// GET or PUT /predict/threshold -> {threshold, default, min, max, step, canEdit}.
// A missing canEdit is view only.
export const thresholdSetting = body => ({
  threshold: numberOr(body?.threshold, THRESHOLD_RANGE.default),
  default: numberOr(body?.default, THRESHOLD_RANGE.default),
  min: numberOr(body?.min, THRESHOLD_RANGE.min),
  max: numberOr(body?.max, THRESHOLD_RANGE.max),
  step: numberOr(body?.step, THRESHOLD_RANGE.step),
  canEdit: body?.canEdit === true
});

// A slider value on the API's grid: clamped to the range, to the nearest step.
export const snapThreshold = (value, { min, max, step } = THRESHOLD_RANGE) => {
  if (!isScore(value)) return min;
  const steps = Math.round((Math.min(max, Math.max(min, value)) - min) / step);
  return Number((min + steps * step).toFixed(1));
};

// How far along its track the slider sits, 0 to 100.
export const rangeFill = (value, { min, max } = THRESHOLD_RANGE) =>
  isScore(value) && max > min ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 0;

// Where a score sits on the drawer's 0-10 scale, 0 to 100.
export const scalePosition = score => (isScore(score) ? Math.min(100, Math.max(0, score * 10)) : 0);

// Default or custom: custom is anything but the default (6.0).
export const thresholdKind = (threshold, defaultThreshold = THRESHOLD_RANGE.default) =>
  isScore(threshold) && threshold !== defaultThreshold ? 'Custom' : 'Default';

// '2026-10-09T01:07:00.000000Z' -> '9 Oct 2026'. Empty or unreadable -> '—'.
export const formatDate = iso => {
  const m = typeof iso === 'string' ? ISO.exec(iso) : null;
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined;
  return month ? `${Number(m[3])} ${month} ${m[1]}` : '—';
};

// '2026-10-09T01:07:00.000000Z' -> '9 Oct 2026, 01:07 UTC'. A date with no time keeps the date.
export const formatDateTime = iso => {
  const date = formatDate(iso);
  const m = date === '—' ? null : ISO.exec(iso);
  return m && m[4] ? `${date}, ${m[4]}:${m[5]} UTC` : date;
};

export const formatCount = n => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '—');

// The scorer's defect classes (Trace v16), in words. The weakness type is the
// scorer's class, not a CWE.
const WEAKNESS = {
  memory_safety: 'Memory safety',
  injection: 'Injection',
  deserialization: 'Deserialization',
  path_traversal: 'Path traversal',
  authn_authz: 'Authentication or authorization',
  cryptography: 'Cryptography',
  race_condition: 'Race condition',
  info_disclosure: 'Information disclosure',
  remote_dos: 'Denial of service',
  input_validation: 'Input validation',
  not_security: 'Not a security defect'
};

// A class the list above doesn't know reads as its own words: 'type_confusion' -> 'Type confusion'.
export const weaknessLabel = defectClass => {
  if (typeof defectClass !== 'string' || !defectClass.trim()) return '—';
  if (WEAKNESS[defectClass]) return WEAKNESS[defectClass];
  const words = defectClass.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// The bug's link, only when it is http(s); anything else (javascript:, data:)
// must never become an href.
export const safeUrl = url => {
  if (typeof url !== 'string') return null;
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
};

// The CVE a bug became, if any. Null until STRY0038972 matches CVEs; read as
// an id string, or an object carrying one.
export const cveId = cve => {
  if (typeof cve === 'string' && cve.trim()) return cve.trim();
  if (cve && typeof cve === 'object' && typeof cve.id === 'string' && cve.id.trim()) return cve.id.trim();
  return null;
};

// The API's promoted flag decides; a bug without one is promoted from 6.0.
export const isPromoted = bug =>
  typeof bug?.promoted === 'boolean' ? bug.promoted : isScore(bug?.score) && bug.score >= PROMOTION_SCORE;

export const bugKey = bug => `${bug.vendor}\u0000${bug.bugId}`;

// How many projects the filter row shows as chips; the rest are under More.
export const TOP_PROJECTS = 6;

// Most bugs first, ties A to Z; or A to Z.
export const sortProjects = (projects, order = 'count') =>
  [...projects].sort((a, b) => (order === 'name' ? 0 : b.count - a.count) || a.name.localeCompare(b.name));

const mostCommon = counts => [...counts.entries()].sort((x, y) => y[1] - x[1]).map(([key]) => key);

const tally = (counts, key) => {
  if (key) counts.set(key, (counts.get(key) || 0) + 1);
};

// The projects on the list, most bugs first (ties A to Z), each with its bug
// count, the source most of its bugs were filed on and its archive vendor ids,
// most bugs first: [{target, name, count, source, vendors}]. Counts are the
// list's, at the threshold it was read at.
export const projectsOf = bugs => {
  const byTarget = new Map();
  bugs.forEach(bug => {
    let project = byTarget.get(bug.target);
    if (!project) {
      project = {
        target: bug.target,
        name: bug.targetName || bug.target,
        count: 0,
        sources: new Map(),
        ids: new Map()
      };
      byTarget.set(bug.target, project);
    }
    project.count += 1;
    tally(project.sources, sourceFromUrl(bug.url));
    tally(project.ids, bug.vendor);
  });
  return sortProjects(
    [...byTarget.values()].map(({ sources, ids, ...project }) => ({
      ...project,
      source: mostCommon(sources)[0] || null,
      vendors: mostCommon(ids)
    }))
  );
};

// The watch list's filters live in its URL (STRY0039087): `vendor`
// (repeatable, a Prevent vendor id or 'sample'), `project` (repeatable, an
// archive vendor id: bug.vendor), `q` (the search), `min` (a lowest score) and
// `weakness` (repeatable, a scorer class such as memory_safety:
// bug.weaknessType; STRY0039127). Other pages link to the list with them.
export const MIN_SCORE_MAX = 9.5;

// A `min` from the URL: a score from 0 to 10, else no minimum.
export const parseMin = value => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 && n <= 10 ? n : null;
};

// URL `project` ids -> the list's projects (targets). An id is a bug's vendor or
// its target; one that matches no bug on the list is dropped.
export const resolveProjects = (ids, bugs) => {
  const targets = [];
  ids.forEach(id => {
    const bug = bugs.find(b => b.target === id) || bugs.find(b => b.vendor === id);
    if (bug && !targets.includes(bug.target)) targets.push(bug.target);
  });
  return targets;
};

// The weakness types on the list, most bugs first (ties A to Z), each with
// its bug count: [{target, name, count}], `target` the scorer's class and
// `name` its words. A bug with no type is in none. Counts are the list's, at
// the threshold it was read at, like the projects'.
export const weaknessesOf = bugs => {
  const counts = new Map();
  bugs.forEach(bug => {
    if (typeof bug.weaknessType === 'string' && bug.weaknessType.trim()) tally(counts, bug.weaknessType);
  });
  return sortProjects(
    [...counts.entries()].map(([type, count]) => ({ target: type, name: weaknessLabel(type), count }))
  );
};

// URL `weakness` values -> the types on the list, each once, in the URL's
// order; one no bug on the list has is dropped.
export const resolveWeaknesses = (types, bugs) =>
  [...new Set(types)].filter(type => bugs.some(bug => weaknessKeys(bug).includes(type)));

// The Vendor filter's value for a bug no vendor's inventory puts in scope: a
// project planted for a demo.
export const SAMPLE_VENDOR = 'sample';

const isId = value => typeof value === 'string' && value !== '';

// The Prevent vendors whose inventory puts a bug (or a dashboard project row)
// in scope, most products first. `inventoryVendorIds` holds all of them; an
// older API sends only `inventoryVendorId`. [] for a planted demo project.
export const vendorIdsOf = item => {
  const ids = Array.isArray(item?.inventoryVendorIds) ? item.inventoryVendorIds.filter(isId) : [];
  if (ids.length) return [...new Set(ids)];
  return isId(item?.inventoryVendorId) ? [item.inventoryVendorId] : [];
};

// A bug's values in each of the list's menus: its vendors (or Sample data),
// its project and its weakness type, if it has one.
function vendorKeys(bug) {
  const ids = vendorIdsOf(bug);
  return ids.length ? ids : [SAMPLE_VENDOR];
}
function weaknessKeys(bug) {
  return typeof bug.weaknessType === 'string' && bug.weaknessType.trim() ? [bug.weaknessType] : [];
}
const MENUS = { vendors: vendorKeys, projects: bug => [bug.target], weaknesses: weaknessKeys };

// A menu's options in the order it lists them: most bugs first, ties A to Z;
// the Vendor menu's Sample data last.
const sortOptions = (options, key) => {
  const sorted = sortProjects(options);
  return key === 'vendors'
    ? sorted.sort((a, b) => (a.target === SAMPLE_VENDOR) - (b.target === SAMPLE_VENDOR))
    : sorted;
};

// The vendors on the list, most bugs first (ties A to Z), each with its bug
// count: [{target, name, count}], `target` the Prevent vendor id and `name`
// from `nameOf`. Bugs no vendor puts in scope are "Sample data", always last.
export const vendorsOf = (bugs, nameOf = id => id) => {
  const counts = new Map();
  bugs.forEach(bug => vendorKeys(bug).forEach(id => tally(counts, id)));
  return sortOptions(
    [...counts.entries()].map(([id, count]) => ({
      target: id,
      name: id === SAMPLE_VENDOR ? 'Sample data' : nameOf(id),
      count
    })),
    'vendors'
  );
};

// URL `vendor` ids -> the vendors on the list, each once, in the URL's order;
// one no bug on the list has is dropped.
export const resolveVendors = (ids, bugs) =>
  [...new Set(ids)].filter(id => bugs.some(bug => vendorKeys(bug).includes(id)));

// How many bugs each value of the `key` menu ('vendors', 'projects' or
// 'weaknesses') would show: the bugs every other filter lets through that have
// it. A bug several vendors ship counts once under each of them.
export const facetCounts = (bugs, filters, key) => {
  const counts = new Map();
  filterBugs(bugs, filters, key).forEach(bug => new Set(MENUS[key](bug)).forEach(value => tally(counts, value)));
  return counts;
};

// A menu's options (from vendorsOf, projectsOf or weaknessesOf) with faceted
// counts, re-sorted by them. An option no bug would show stays, at 0.
export const menuOptions = (options, bugs, filters, key) => {
  const counts = facetCounts(bugs, filters, key);
  return sortOptions(
    options.map(option => ({ ...option, count: counts.get(option.target) || 0 })),
    key
  );
};

// Each vendor's monogram shade ("BugZero soft"), every one a BugZero token:
// brand blues and tans only, never the status colours, which mean score
// severity on these pages.
export const VENDOR_SHADES = [
  { bg: 'var(--plan-soft)', ink: 'var(--bz-blue-1)', line: 'var(--plan-soft)' },
  { bg: 'var(--bz-tan-2)', ink: 'var(--bz-blue-2)', line: 'var(--bz-tan-2)' },
  { bg: 'var(--predict-soft)', ink: 'var(--predict-ink)', line: 'var(--line-strong)' },
  { bg: 'var(--bg-tan)', ink: 'var(--bz-blue-1)', line: 'var(--bz-tan-3)' },
  { bg: 'var(--bz-tan-1)', ink: 'var(--bz-blue-3)', line: 'var(--line)' },
  { bg: 'var(--bg)', ink: 'var(--bz-blue-2)', line: 'var(--bz-blue-3)' }
];

// The vendors on the loaded list, A to Z by id, take the shades in turn, so no
// two look alike until a seventh; their letters still tell them apart.
export const vendorShades = items => {
  const ids = [...new Set(items.flatMap(vendorIdsOf))].sort((a, b) => a.localeCompare(b));
  return new Map(ids.map((id, index) => [id, VENDOR_SHADES[index % VENDOR_SHADES.length]]));
};

// A vendor's monogram: the initials of a name of two words or more, else its
// first two letters. 'Red Hat' -> 'RH', 'F5' -> 'F5', 'Cisco' -> 'CI'.
export const monogram = name => {
  const words = String(name || '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  return (words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2)).toUpperCase();
};

const texts = value => (Array.isArray(value) ? value.filter(isId) : []);

// Why an item is in scope, one block per vendor in vendorIdsOf's order:
// [{vendorId, products, sources}]. `inventory` is the list's, keyed by archive
// vendor id; `archiveIds` are the item's (a bug's `vendor`, a dashboard row's
// `vendors`). A vendor `inventory` doesn't name (an older API sends none)
// keeps `inventoryProducts` if it is the item's `inventoryVendorId`. [] for a
// planted demo project.
export const vendorBlocks = (item, inventory, archiveIds = [item?.vendor]) => {
  const known = archiveIds.flatMap(id => (Array.isArray(inventory?.[id]) ? inventory[id] : [])).filter(Boolean);
  return vendorIdsOf(item).map(vendorId => {
    const uses = known.filter(use => use.vendorId === vendorId);
    if (!uses.length) {
      const products = vendorId === item.inventoryVendorId ? texts(item.inventoryProducts) : [];
      return { vendorId, products, sources: [] };
    }
    return {
      vendorId,
      products: [...new Set(uses.flatMap(use => texts(use.products)))],
      sources: [...new Set(uses.flatMap(use => texts(use.sources)))]
    };
  });
};

const SHOWN_PRODUCTS = 6;

// "3 products", "1 product".
export const productCount = n => `${n} ${n === 1 ? 'product' : 'products'}`;

// The first products a vendor found the project inside, "+N more" for the
// rest; a vendor that found none put its own product in scope.
export const productsLine = products => {
  if (!products.length) return 'The vendor product itself';
  const more = products.length - SHOWN_PRODUCTS;
  return `${products.slice(0, SHOWN_PRODUCTS).join(', ')}${more > 0 ? ` +${more} more` : ''}`;
};

// The selected projects as URL `project` ids: each one's archive vendor ids.
export const projectIds = (selected, projects) =>
  selected.flatMap(target => projects.find(project => project.target === target)?.vendors || []);

// The `min` choices above the threshold, threshold + 0.5 to 9.5, plus a URL value off that grid.
export const minScoreOptions = (threshold, current) => {
  const start = isScore(threshold) ? threshold : PROMOTION_SCORE;
  const options = [];
  for (let step = 1; start + step * 0.5 <= MIN_SCORE_MAX; step += 1)
    options.push(Number((start + step * 0.5).toFixed(1)));
  if (isScore(current) && !options.includes(current)) options.push(current);
  return options.sort((a, b) => a - b);
};

// Settings' what-if: how many of the list's bugs a higher threshold takes off
// it. Null when it doesn't rise: a lower one needs bugs the list doesn't hold.
export const removedBy = (bugs, from, to) =>
  isScore(from) && isScore(to) && to > from ? bugs.filter(bug => isScore(bug.score) && bug.score < to).length : null;

// The filter row's chips: the top projects, then any selected project outside
// them, so a selection made under More stays in view.
export const rowProjects = (projects, selected, top = TOP_PROJECTS) => {
  const shown = projects.slice(0, top);
  return [...shown, ...projects.slice(top).filter(project => selected.includes(project.target))];
};

// The projects More holds beyond the chips; 0 hides it.
export const moreCount = (projects, top = TOP_PROJECTS) => Math.max(0, projects.length - top);

// Projects whose name or source matches the search, case-insensitively.
export const searchProjects = (projects, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return projects;
  return projects.filter(project =>
    [project.name, project.target, project.source ? sourceLabel(project.source) : '']
      .filter(Boolean)
      .some(text => text.toLowerCase().includes(q))
  );
};

// The selection's size on the list: {projects, bugs}.
export const selectionSummary = (projects, selected) => {
  const chosen = projects.filter(project => selected.includes(project.target));
  return { projects: chosen.length, bugs: chosen.reduce((sum, project) => sum + project.count, 0) };
};

// Where the watch list and a bug's own page live (STRY0039090 routes them).
export const WATCH_PATH = '/predict/watch';
// The watch list's name wherever the portal shows it: the menu, the top bar, the page, a bug's back
// link, Home's Predict card and Settings.
export const WATCH_LABEL = 'Bugs Watch list';
export const bugPagePath = (vendor, bugId) =>
  `${WATCH_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(bugId)}`;

const searchText = bug =>
  [bug.title, bug.bugId, bug.targetName, bug.reason, cveId(bug.cve)]
    .filter(value => typeof value === 'string')
    .join('\n')
    .toLowerCase();

// The bugs that match the search, score at least `min` (when one is set) and
// match each menu: any of its selected vendors (SAMPLE_VENDOR for a bug with
// none), projects or weakness types; a menu with nothing selected lets every
// bug through. `skip` names a menu to leave out, for its own counts.
export const filterBugs = (bugs, { query, vendors = [], projects = [], weaknesses = [], min = null }, skip = null) => {
  const q = (query || '').trim().toLowerCase();
  const selected = { vendors, projects, weaknesses };
  return bugs.filter(
    bug =>
      Object.keys(MENUS).every(
        key => key === skip || !selected[key].length || MENUS[key](bug).some(value => selected[key].includes(value))
      ) &&
      (!q || searchText(bug).includes(q)) &&
      (!isScore(min) || (isScore(bug.score) && bug.score >= min))
  );
};

// Score or filed date, either way. Ties keep the API's order (score descending,
// newest freeze first), so the default sort is exactly the API's. A bug with no
// filing date goes last either way.
export const sortBugs = (bugs, { key, dir }) => {
  const sign = dir === 'asc' ? 1 : -1;
  const compare = (a, b) => {
    if (key === 'filed') {
      const fa = typeof a.filed === 'string' ? a.filed : null;
      const fb = typeof b.filed === 'string' ? b.filed : null;
      if (fa === null || fb === null) return (fa === null) - (fb === null);
      return sign * fa.localeCompare(fb);
    }
    return sign * ((isScore(a.score) ? a.score : 0) - (isScore(b.score) ? b.score : 0));
  };
  return bugs
    .map((bug, index) => ({ bug, index }))
    .sort((a, b) => compare(a.bug, b.bug) || a.index - b.index)
    .map(({ bug }) => bug);
};

// The index of the row before which the "not promoted" band goes: the first
// bug that isn't promoted, when the list is sorted by score from the top. -1
// for no band.
export const bandIndex = (rows, sort) =>
  sort.key === 'score' && sort.dir === 'desc' ? rows.findIndex(bug => !isPromoted(bug)) : -1;

export const scoreTone = score => {
  if (!isScore(score)) return 'muted';
  if (score >= 7.5) return 'bad';
  if (score >= 7.0) return 'prevent';
  if (score >= PROMOTION_SCORE) return 'warn';
  return 'muted';
};

export const CSV_HEADER = [
  'Score',
  'Promoted',
  'OSS',
  'Vendors',
  'Bug ID',
  'Title',
  'Link',
  'Filed',
  'Rationale',
  'CVE',
  'Weakness type'
];

// A cell a spreadsheet would run as a formula gets a leading quote. Titles and
// reasons come from public trackers, so anyone can file one that starts with '='.
const FORMULA_START = /^[=+\-@\t\r]/;

const csvCell = value => {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = FORMULA_START.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

// The rows as they are on screen, in their order: the same score, project,
// title, date and reason text, the vendors by name (`nameOf`; "Sample" for a
// planted demo project) and the weakness type in words. A missing date, link
// or type is an empty cell.
export const toCsv = (rows, nameOf = id => id) => {
  const lines = rows.map(bug => {
    const filed = formatDate(bug.filed);
    const vendors = vendorIdsOf(bug).map(nameOf);
    return [
      formatScore(bug.score),
      isPromoted(bug) ? 'Yes' : 'No',
      bug.targetName || bug.target,
      vendors.length ? vendors.join(', ') : 'Sample',
      bug.bugId,
      bug.title,
      safeUrl(bug.url) || '',
      filed === '—' ? '' : filed,
      bug.reason,
      cveId(bug.cve) || '',
      weaknessLabel(bug.weaknessType) === '—' ? '' : weaknessLabel(bug.weaknessType)
    ]
      .map(csvCell)
      .join(',');
  });
  return [CSV_HEADER.map(csvCell).join(','), ...lines].join('\r\n');
};

// predict-watch-list-2026-10-27.csv, dated in the browser's own day.
export const csvFileName = (now = new Date()) => {
  const pad = n => String(n).padStart(2, '0');
  return `predict-watch-list-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
};

// What BugZero tracks (STRY0039127), each number with its own label, so an
// archived bug is never read as a scored one: the bugs in BugZero's archive,
// the customer's projects' share of them, and the bugs Predict has scored and
// promoted since its first run. `counts` is GET /predict/counts; `projects`
// is the watch list's `archive`, {bugs, asOf}. A number not known is null.
export const trackedNumbers = (counts, projects) => {
  const since = formatDate(counts?.since);
  const count = n => (typeof n === 'number' && Number.isFinite(n) ? n : null);
  return [
    {
      key: 'archive',
      label: "Bugs in BugZero's archive",
      value: count(counts?.archive?.bugs),
      note: 'Collected from every source BugZero watches'
    },
    {
      key: 'projects',
      label: 'Archived from your OSS',
      value: count(projects?.bugs),
      note: "Bugs in BugZero's archive from the OSS in your scope"
    },
    {
      key: 'scored',
      label: since === '—' ? 'Scored by Predict' : `Scored by Predict since ${since}`,
      value: count(counts?.scored),
      note: 'New bugs, for all customers'
    },
    {
      key: 'promoted',
      label: 'Promoted by Predict',
      value: count(counts?.promoted),
      note: `Scored ${formatScore(PROMOTION_SCORE)} or higher, for all customers`
    }
  ];
};
